"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { ArrowRight, Scale, ListChecks, BadgeInfo, BookOpen } from "lucide-react";
import { PORTFOLIO } from "../data";
import { ixTrack } from "../analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";

/** eSIM Index — "Best for" intent routing cards (transparent recommendations). */
export function BestForRouting() {
  const { t } = useTranslations();
  const picks = PORTFOLIO.slice(0, 6);
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="h2" className="text-2xl sm:text-3xl mb-1" y={12}>{t("ix.bestfor.title", "Best for your trip")}</Reveal>
      <p className="text-sm text-muted-foreground mb-5">{t("ix.bestfor.sub", "Pick by what matters most to you — we explain why in our methodology.")}</p>
      <Stagger as="div" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" staggerChildren={0.06}>
        {picks.map((p) => (
          <StaggerItem as="div" key={p.key} y={12}>
            <Link href={p.href} onClick={() => ixTrack.outboundReferralClicked(p.key, p.bestFor)} className="ix-card block p-5 h-full transition-all hover:-translate-y-0.5 hover:border-primary/40">
              <span className="ix-badge mb-2">{p.bestFor}</span>
              <div className="font-bold text-foreground">{p.name}</div>
              <p className="text-sm text-muted-foreground mt-1">{p.highlight}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary">{t("ix.bestfor.see", "See plans")} <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

/** eSIM Index — methodology teaser. */
export function MethodologyTeaser() {
  const { t } = useTranslations();
  const factors = [
    t("ix.method.f1", "Price per GB & value"),
    t("ix.method.f2", "Coverage & networks"),
    t("ix.method.f3", "Validity & data size"),
    t("ix.method.f4", "Ease of activation"),
    t("ix.method.f5", "Hotspot & support"),
    t("ix.method.f6", "Refund & transparency"),
  ];
  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="ix-card p-6 sm:p-8 grid lg:grid-cols-[1fr_1.2fr] gap-6 items-center" y={20}>
        <div>
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-3"><Scale className="w-6 h-6" /></span>
          <h2 className="text-2xl">{t("ix.method.title", "How we rank")}</h2>
          <p className="mt-2 text-muted-foreground">{t("ix.method.sub", "Our rankings weigh objective plan attributes, not affiliate payouts. When our own experiences rank well, we explain why.")}</p>
          <Link href="/installation-guide/" onClick={() => ixTrack.methodologyViewed("home_teaser")} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline">{t("ix.method.cta", "Read the full methodology")} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <Stagger as="ul" className="grid sm:grid-cols-2 gap-3" staggerChildren={0.05}>
          {factors.map((f) => <StaggerItem as="li" key={f} y={8} className="flex items-center gap-2 text-sm text-foreground/80"><ListChecks className="w-4 h-4 text-primary shrink-0" />{f}</StaggerItem>)}
        </Stagger>
      </Reveal>
    </section>
  );
}

/** eSIM Index — glossary + FAQ. */
export function FaqGlossary() {
  const { t } = useTranslations();
  const glossary = [
    { term: t("ix.glos.t1", "Price per GB"), def: t("ix.glos.d1", "Plan price divided by data — the clearest way to compare value.") },
    { term: t("ix.glos.t2", "Regional eSIM"), def: t("ix.glos.d2", "One plan covering multiple countries in a region.") },
    { term: t("ix.glos.t3", "Hotspot"), def: t("ix.glos.d3", "Sharing your eSIM data with other devices (tethering).") },
    { term: t("ix.glos.t4", "Validity"), def: t("ix.glos.d4", "How long a plan stays active after first connection.") },
  ];
  const faqs = [
    { q: t("ix.faq.q1", "Is this comparison biased?"), a: t("ix.faq.a1", "We rank by objective plan attributes and publish our methodology. We disclose where recommendations point to our own experiences.") },
    { q: t("ix.faq.q2", "Are prices up to date?"), a: t("ix.faq.a2", "Our own catalog pricing is live. Competitor figures are only shown from a maintained, dated dataset.") },
    { q: t("ix.faq.q3", "Why is one option ranked first?"), a: t("ix.faq.a3", "Because it best fits the stated use-case — the 'best for' column explains each pick.") },
  ];
  return (
    <section className="container mx-auto px-4 py-10 grid lg:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 mb-4"><BookOpen className="w-5 h-5 text-primary" /><h2 className="text-2xl">{t("ix.glos.title", "Glossary")}</h2></div>
        <Stagger as="div" className="space-y-3" staggerChildren={0.05}>
          {glossary.map((g) => <StaggerItem as="div" key={g.term} y={8} className="ix-card p-4"><div className="font-bold text-foreground text-sm">{g.term}</div><div className="text-sm text-muted-foreground">{g.def}</div></StaggerItem>)}
        </Stagger>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-4"><BadgeInfo className="w-5 h-5 text-primary" /><h2 className="text-2xl">{t("ix.faq.title", "Comparison FAQ")}</h2></div>
        <Stagger as="div" className="space-y-3" staggerChildren={0.05}>
          {faqs.map(({ q, a }) => (
            <StaggerItem as="div" key={q} y={8}>
              <details className="group ix-card p-4">
                <summary className="flex items-center justify-between cursor-pointer font-semibold text-foreground list-none">{q}<span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none">+</span></summary>
                <p className="mt-2 text-sm text-muted-foreground">{a}</p>
              </details>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/** eSIM Index — affiliate/independence disclosure (mandatory per instructions). */
export function Disclosure() {
  const { t } = useTranslations();
  return (
    <section className="container mx-auto px-4 pb-14">
      <div className="ix-card p-5 flex items-start gap-3">
        <BadgeInfo className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">
          {t("ix.disclosure.text", "Disclosure: eSIM Index recommends eSIM experiences from our own portfolio and tracks selected market providers. Recommendations are based on plan attributes and our published methodology, not on payment. We may earn a commission when you buy through some links.")}
          {" "}
          <Link href="/contact-us/" className="font-bold text-primary hover:underline">{t("ix.disclosure.more", "Read our full disclosure")}</Link>
        </p>
      </div>
    </section>
  );
}
